import firebase from "firebase/app";
import useSWR from "swr";

import type { Event } from "@spwn/types/firebase/firestore";

export type SalesDashboardEvent = {
  eventId: string;
  eventName: string;
};

type SalesDashboardEventDocument = {
  eventId: string;
  event:
    | {
        title: string;
      }
    | undefined;
};

/**
 * Firestoreの取得結果を、売上ダッシュボードで使うイベント情報へ変換する。
 * 存在しないドキュメントは`event: undefined`として除外するため、同時に取得できた
 * 他のイベントはそのまま利用できる。
 */
const createSalesDashboardEvents = (
  documents: readonly SalesDashboardEventDocument[]
): SalesDashboardEvent[] =>
  documents.flatMap(({ eventId, event }) => {
    if (!event?.title) {
      return [];
    }

    return [
      {
        eventId,
        eventName: event.title.replace(/<(\/|)br ?(\/|)>/g, "\n"),
      },
    ];
  });

const GetSalesDashboardEventsKey = "getSalesDashboardEvents";

export const useGetSalesDashboardEvents = (eventIds: string[]) => {
  const currentUserId = firebase.auth().currentUser?.uid ?? null;
  const uniqueEventIds = Array.from(new Set(eventIds)).sort();
  const uniqueEventIdsKey = JSON.stringify(uniqueEventIds);

  const {
    data: events = [],
    isLoading,
    isValidating,
    error,
    mutate,
  } = useSWR<SalesDashboardEvent[], Error>(
    currentUserId && uniqueEventIds.length > 0
      ? [GetSalesDashboardEventsKey, currentUserId, uniqueEventIdsKey]
      : null,
    async () => {
      const snapshots = await Promise.all(
        uniqueEventIds.map(async (eventId) => {
          return firebase.firestore().collection("events").doc(eventId).get();
        })
      );

      return createSalesDashboardEvents(
        snapshots.map((snapshot) => {
          if (!snapshot.exists) {
            // Firestoreは存在しないドキュメントも`exists: false`のsnapshotとして返す。
            return {
              eventId: snapshot.id,
              event: undefined,
            };
          }

          return {
            eventId: snapshot.id,
            event: snapshot.data() as Event | undefined,
          };
        })
      );
    }
  );

  return {
    events,
    eventNames: events.map((event) => event.eventName),
    isLoading,
    isValidating,
    error,
    mutate,
  };
};
